import {
  Button,
  ControlledInput,
  GenericInput as Input,
  Modal,
  ModalContent,
  ModalTitle,
  TableBase,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow
} from '@hm/fundamental'
import {mdiCheck, mdiClose, mdiFileUpload, mdiTune} from '@mdi/js'
import {useCallback, useState} from 'react'
import {Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useUpdateDrillingForm} from './hooks/useUpdateDrillingModalForm'
import {UploadDrillingResultsDialog} from './UploadDrillingResultsDialog'

import {useKilns} from '@refra/modules/common/providers'
import {getKilnSetupRedirectUrl} from '@refra/modules/common/utils'
import {DRILLING_SETTINGS_CANVAS_HEIGHT} from '@refra/modules/refra-planning/components/KilnOverviewCard/Drilling/DrillingSettings/DrillingZones/helpers'
import type {
  DrillingPosition,
  KilnShutdown,
  ParsedDrillingData,
  ShutdownDrillingDTO
} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

const DRILLING_MODAL_METERS_OFFSET = 5

interface DrillingModalProps {
  drillings: ShutdownDrillingDTO[]
  drillingPositions: DrillingPosition[]
  meter: number
  kilnLength: number
  shutdown: KilnShutdown
}

export const DrillingModal = ({
  drillings,
  drillingPositions,
  kilnLength,
  meter,
  shutdown
}: DrillingModalProps) => {
  const {t} = useTranslation()
  const plantId = useUrlParam('plantCode')
  const {selectedKiln} = useKilns()

  const {fields, onClose, formMethods, onSubmit} = useUpdateDrillingForm({
    kilnLength,
    drillingPositions,
    shutdown,
    drillings
  })

  const fromMeter = meter - DRILLING_MODAL_METERS_OFFSET
  const [from, setFrom] = useState(
    fromMeter > 0 ? (fromMeter < kilnLength ? fromMeter : kilnLength) : 1
  )

  const toMeter = meter + DRILLING_MODAL_METERS_OFFSET
  const [to, setTo] = useState(toMeter > kilnLength ? kilnLength : toMeter > 0 ? toMeter : 1)

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)

  const handleDataImported = useCallback(
    (data: ParsedDrillingData) => {
      // Map parsed data to form fields
      const enabledPositions = drillingPositions.filter((p) => p.isEnabled)

      for (const row of data) {
        const meterIndex = row.meter - 1 // Convert 1-based meter to 0-based index
        if (meterIndex < 0 || meterIndex >= kilnLength) continue

        for (const {positionName, depth} of row.values) {
          // Find the position index by matching the position name
          const enabledPositionIndex = enabledPositions.findIndex(
            (p) => p.name.toLowerCase() === positionName.toLowerCase()
          )
          if (enabledPositionIndex === -1 || depth === null) continue

          // Find actual position index in the full positions array
          const positionIndex = drillingPositions.findIndex(
            (p) => p.name.toLowerCase() === positionName.toLowerCase()
          )
          if (positionIndex === -1) continue

          // Set the value in the form
          formMethods.setValue(`drilling.${meterIndex}.positions.${positionIndex}.depth`, depth)
        }
      }
    },
    [drillingPositions, kilnLength, formMethods]
  )

  const tableHeaders = [{key: 'meter', label: t('common.meter')}]
  drillingPositions
    ?.filter((p) => p.isEnabled)
    .forEach((drillingPosition) =>
      tableHeaders.push({key: drillingPosition.name, label: drillingPosition.name})
    )

  const navigateToSettings = () =>
    (window.location.href = getKilnSetupRedirectUrl(plantId, selectedKiln.upmId, 'drillingZones'))

  return (
    <>
      <Modal open onClose={onClose} data-cy="refra_add_drilling_modal" width={'wide'}>
        <form onSubmit={onSubmit} className="space-y-4">
          <ModalContent>
            <div className={'flex justify-between text-center mb-5'}>
              <ModalTitle data-cy="refra_add_drilling_modal_title">
                {t('refraPlanning.drilling.add')}
              </ModalTitle>
              <div className={'space-x-2 flex justify-end'}>
                <Button
                  type="button"
                  iconPrefix={mdiFileUpload}
                  variant={'secondary'}
                  data-cy="upload_drilling_results_btn"
                  onClick={() => setIsUploadDialogOpen(true)}
                >
                  {t('refraPlanning.drilling.uploadResults')}
                </Button>
                <Button
                  type="button"
                  iconPrefix={mdiTune}
                  variant={'secondary'}
                  data-cy="edit_drilling_position_btn"
                  onClick={() => navigateToSettings()}
                >
                  {t('refraPlanning.drilling.edit')}
                </Button>
                <Button
                  type="button"
                  iconPrefix={mdiClose}
                  variant={'secondary'}
                  data-cy="cancel_drilling_btn"
                  onClick={() => onClose()}
                >
                  {t('common.cancel')}
                </Button>
                <Button iconPrefix={mdiCheck} type="submit" data-cy="add_drilling_btn">
                  {t('common.save')}
                </Button>
              </div>
            </div>
            <div className={'flex justify-start mb-4 gap-4'}>
              <Input
                name={'fromMeter'}
                value={from.toString()}
                type={'number'}
                step={1}
                max={to}
                min={1}
                label={t('refraPlanning.drilling.from')}
                onChange={(value) => setFrom(Number(value))}
                data-cy="drilling_from_input"
              />
              <Input
                name={'toMeter'}
                value={to.toString()}
                type={'number'}
                step={1}
                max={kilnLength}
                min={from}
                label={t('refraPlanning.drilling.to')}
                onChange={(value) => setTo(Number(value))}
                data-cy="drilling_to_input"
              />
            </div>

            <TableBase>
              <TableHeader data-cy="drillings_table_header">
                {tableHeaders.map(({key, label}) => {
                  return (
                    <TableHeaderCell key={key} className="text-center">
                      {label}
                    </TableHeaderCell>
                  )
                })}
              </TableHeader>
              <TableBody>
                {fields.map((field, index) => {
                  const kilnMeter = index + 1
                  return (
                    kilnMeter >= from &&
                    kilnMeter <= to && (
                      <TableRow
                        key={`drilling.${kilnMeter}.positions`}
                        data-cy={`drilling.${kilnMeter}.positions`}
                      >
                        <TableCell
                          key={`drilling.${kilnMeter}.meter`}
                          data-cy={`drilling.${kilnMeter}.meter`}
                          className="text-center"
                        >
                          {kilnMeter}
                        </TableCell>
                        {field.positions.map((position, pIndex) =>
                          position.isEnabled ? (
                            <TableCell
                              key={`drilling.${kilnMeter}.positions.${pIndex}.depth`}
                              data-cy={`drilling.${kilnMeter}.positions.${pIndex}.depth`}
                            >
                              <Controller
                                name={`drilling.${index}.positions.${pIndex}.depth`}
                                control={formMethods.control}
                                render={({field}) => (
                                  <ControlledInput
                                    {...field}
                                    value={field.value ? field.value.toString() : ''}
                                    label={''}
                                    min={0}
                                    step={0.1}
                                    max={DRILLING_SETTINGS_CANVAS_HEIGHT}
                                    type={'number'}
                                    onChange={(e) => {
                                      field.onChange(Number(e.target.value))
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                          ) : null
                        )}
                      </TableRow>
                    )
                  )
                })}
              </TableBody>
            </TableBase>
          </ModalContent>
        </form>
      </Modal>
      <UploadDrillingResultsDialog
        isOpen={isUploadDialogOpen}
        closeDialog={() => setIsUploadDialogOpen(false)}
        shutdownId={shutdown.id}
        shutdownName={shutdown.name}
        drillingPositions={drillingPositions}
        drillings={drillings}
        onDataImported={handleDataImported}
        kilnLength={kilnLength}
      />
    </>
  )
}
